import type { DbClient } from "@tanstack/db";
import { getAppDbClient } from "~/lib/ssr/runtime";
import type { AssetDomainCollections } from "./collection-registry";
import type {
  PendingCanonicalEquityQuote,
  PendingCanonicalMarketQuote,
} from "./collection-types";

function createSessionState() {
  const browserAssetDomainCollectionsBySession = new Map<
    string,
    AssetDomainCollections
  >();
  const pendingCanonicalMarketQuotesBySession = new Map<
    string,
    Map<string, PendingCanonicalMarketQuote>
  >();
  const pendingCanonicalEquityQuotesBySession = new Map<
    string,
    Map<string, PendingCanonicalEquityQuote>
  >();

  const generations = new Map<string, number>();
  return {
    browserAssetDomainCollectionsBySession,
    pendingCanonicalMarketQuotesBySession,
    pendingCanonicalEquityQuotesBySession,
    generations,
    publicCanonicalGeneration: 0,
  };
}
const states = new WeakMap<DbClient, ReturnType<typeof createSessionState>>();
function getSessionState() {
  const db = getAppDbClient();
  let state = states.get(db);
  if (!state) {
    state = createSessionState();
    states.set(db, state);
  }
  return state;
}

export function readCachedAssetDomainCollections(
  sessionScopeToken: string,
): AssetDomainCollections | undefined {
  return getSessionState().browserAssetDomainCollectionsBySession.get(
    sessionScopeToken,
  );
}

export function cacheAssetDomainCollections(
  sessionScopeToken: string,
  collections: AssetDomainCollections,
): void {
  getSessionState().browserAssetDomainCollectionsBySession.set(
    sessionScopeToken,
    collections,
  );
}

export function getPendingCanonicalMarketQuotes(
  sessionScopeToken: string,
): Map<string, PendingCanonicalMarketQuote> {
  const existing =
    getSessionState().pendingCanonicalMarketQuotesBySession.get(
      sessionScopeToken,
    );
  if (existing) {
    return existing;
  }

  const created = new Map<string, PendingCanonicalMarketQuote>();
  getSessionState().pendingCanonicalMarketQuotesBySession.set(
    sessionScopeToken,
    created,
  );
  return created;
}

export function readPendingCanonicalMarketQuotes(
  sessionScopeToken: string,
): ReadonlyMap<string, PendingCanonicalMarketQuote> | undefined {
  return getSessionState().pendingCanonicalMarketQuotesBySession.get(
    sessionScopeToken,
  );
}

export function getPendingCanonicalEquityQuotes(
  sessionScopeToken: string,
): Map<string, PendingCanonicalEquityQuote> {
  const existing =
    getSessionState().pendingCanonicalEquityQuotesBySession.get(
      sessionScopeToken,
    );
  if (existing) {
    return existing;
  }

  const created = new Map<string, PendingCanonicalEquityQuote>();
  getSessionState().pendingCanonicalEquityQuotesBySession.set(
    sessionScopeToken,
    created,
  );
  return created;
}

export function readPendingCanonicalEquityQuotes(
  sessionScopeToken: string,
): ReadonlyMap<string, PendingCanonicalEquityQuote> | undefined {
  return getSessionState().pendingCanonicalEquityQuotesBySession.get(
    sessionScopeToken,
  );
}

export function resetAssetDomainSessionState(sessionScopeToken?: string): void {
  const state = getSessionState();
  const retired =
    sessionScopeToken === undefined
      ? [...state.browserAssetDomainCollectionsBySession.keys()]
      : [sessionScopeToken];
  for (const scope of retired) {
    // A retired descriptor must never resolve to an old principal's rows.
    state.generations.set(scope, (state.generations.get(scope) ?? 0) + 1);
    // React may still be detaching the previous scope's live queries. Let
    // TanStack DB reclaim their sources after the last dependent unsubscribes;
    // manual cleanup here would put those live queries into an error state.
  }
  if (sessionScopeToken === undefined) {
    // Full runtime/test resets also retire public data. Auth/VM transitions
    // pass a scope and must retain public instrument identity and quotes.
    state.publicCanonicalGeneration += 1;
    getSessionState().browserAssetDomainCollectionsBySession.clear();
    getSessionState().pendingCanonicalMarketQuotesBySession.clear();
    getSessionState().pendingCanonicalEquityQuotesBySession.clear();
    return;
  }

  getSessionState().browserAssetDomainCollectionsBySession.delete(
    sessionScopeToken,
  );
  getSessionState().pendingCanonicalMarketQuotesBySession.delete(
    sessionScopeToken,
  );
  getSessionState().pendingCanonicalEquityQuotesBySession.delete(
    sessionScopeToken,
  );
}

export function getAssetDomainCollectionId(id: string, scope: string): string {
  const generation = getSessionState().generations.get(scope) ?? 0;
  return generation === 0 ? id : `${id}:generation:${generation}`;
}

export function getPublicCanonicalCollectionId(): string {
  const id = "asset-domain-universe-canonical:public";
  const generation = getSessionState().publicCanonicalGeneration;
  return generation === 0 ? id : `${id}:generation:${generation}`;
}
